import { CheckOutlined, ExportOutlined, EyeOutlined, InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Flex, Input, Popover, Table, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { Price } from '../../../components/Price.tsx';
import { TableColumnSettings } from '../../../components/TableColumnSettings';
import { TableList } from '../../../components/TableList.tsx';
import { type ParametersQuery, PartnerLab } from '../../../graphql/generated/graphql.ts';
import type { TableColumnSetting } from '../../../hooks/store/useUserSettingsStore.ts';
import { useTableHeight } from '../../../hooks/useTableHeight';
import { humanizedHours } from '../../../utils/dateFormatUtils';
import { ellipsis } from '../../../utils/ellipsis';
import { translatePartnerLab } from '../../../utils/enumHelpers';
import { openExternalLink } from '../../../utils/linkOpener';
import { PARAMETERS_QUERY } from './Parameters.gql';
import { CreateParameterModal } from './parameters/CreateParameterModal.tsx';
import { UpdateParameterModal } from './parameters/UpdateParameterModal.tsx';

const { useToken } = theme;

type Parameter = NonNullable<ParametersQuery['labParameters']>[number];

export const Parameters: React.FC = () => {
  const { token } = useToken();
  const { id: labId } = useParams<{ id: string }>();
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredParameters, setFilteredParameters] = useState<Parameter[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [columnSettings, setColumnSettings] = useState<TableColumnSetting[]>([
    { id: 'shortName', visible: true, title: 'Kurzbezeichnung', checkable: false },
    { id: 'longName', visible: true, title: 'Langbezeichnung', checkable: true },
    { id: 'edifactNumber', visible: false, title: 'Edifact-Nummer', checkable: true },
    { id: 'loincCode', visible: true, title: 'LOINC Code', checkable: true },
    { id: 'specimens', visible: true, title: 'Material', checkable: true },
    { id: 'storagePeriod', visible: false, title: 'Lagerdauer', checkable: true },
    { id: 'evalHours', visible: false, title: 'Auswertungsdauer', checkable: true },
    { id: 'doctorBilling', visible: true, title: 'Privat an Zuweiser', checkable: true },
    { id: 'patientBilling', visible: true, title: 'Privat an Patient', checkable: true },
    { id: 'lgDoctorBilling', visible: false, title: 'LG - Privat an Zuweiser', checkable: true },
    { id: 'importantDescription', visible: true, title: 'Wichtige Informationen', checkable: true },
    { id: 'description', visible: true, title: 'Beschreibung', checkable: true },
    { id: 'billingDescription', visible: true, title: 'Verrechnungsinformationen', checkable: true },
    { id: 'aiDescription', visible: true, title: 'KI-Beschreibung', checkable: true },
    { id: 'link', visible: false, title: 'Link', checkable: true },
    { id: 'deactivated', visible: true, title: 'Aktiv', checkable: true },
    { id: 'withParameters', visible: false, title: 'Nur mit', checkable: true },
    { id: 'withoutParameters', visible: false, title: 'Nicht mit', checkable: true },
    { id: 'specialRates', visible: false, title: 'Sondertarife', checkable: true },
    { id: 'forms', visible: false, title: 'Formulare', checkable: true },
    { id: 'priceDoctor', visible: true, title: 'Preis Zuweiser', checkable: true },
    { id: 'pricePatient', visible: true, title: 'Preis Patient', checkable: true },
    { id: 'priceLgDoctor', visible: false, title: 'Preis LG-Zuweiser', checkable: true },
    { id: 'priceDeductible', visible: false, title: 'Preis Selbstbehalt', checkable: true },
    { id: 'partnerLab', visible: false, title: 'Partnerlabor', checkable: true },
    { id: 'synonyms', visible: false, title: 'Synonyme', checkable: true },
    { id: 'acute', visible: true, title: 'Akut', checkable: true },
    { id: 'volume', visible: false, title: 'Volumen', checkable: true },
    { id: 'analysisStations', visible: false, title: 'Analysestationen', checkable: true },
  ]);

  const contentRef = useRef<HTMLDivElement>(null);
  const tableHeight = useTableHeight(contentRef);

  const { data, loading, refetch } = useQuery(PARAMETERS_QUERY, {
    variables: {
      labId: labId ?? '',
    },
    pollInterval: 60000,
  });

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearchValue(value);
    setFilteredParameters(
      filterByTokens(data?.labParameters ?? [], value, [
        p => p.shortName,
        p => p.longName,
        p => p.edifactNumber?.toString(),
        p => p.synonyms,
      ])
    );
  }, 300);

  useEffect(() => {
    if (data) {
      debouncedSearch(searchValue);
    }
  }, [data, searchValue, debouncedSearch]);

  const columns: ColumnsType<Parameter> = [
    {
      title: 'Kurzbez.',
      dataIndex: 'shortName',
      key: 'shortName',
      defaultSortOrder: 'ascend',
      width: 130,
      sorter: (a, b) => a.shortName.localeCompare(b.shortName),
      render: value => (
        <span className={monoFont}>
          <Markable tokens={searchValue ?? ''}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'Langbez.',
      dataIndex: 'longName',
      key: 'longName',
      width: 130,
      ellipsis: true,
      sorter: (a, b) => a.longName.localeCompare(b.longName),
      render: value => (
        <Markable tokens={searchValue ?? ''}>
          <Tooltip title={value} trigger={value.length <= 25 ? [] : ['hover']}>
            {ellipsis(value, 25)}
          </Tooltip>
        </Markable>
      ),
    },
    {
      title: 'Edifact-Nr.',
      dataIndex: 'edifactNumber',
      key: 'edifactNumber',
      width: 120,
      sorter: (a, b) => (a.edifactNumber ?? 0) - (b.edifactNumber ?? 0),
      render: value => (
        <span className={monoFont}>
          <Markable tokens={searchValue ?? ''}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'LOINC',
      dataIndex: 'loincCode',
      key: 'loincCode',
      width: 100,
      sorter: (a, b) => (a.loincCode ?? '').localeCompare(b.loincCode ?? ''),
      render: value =>
        value ? (
          <span className={monoFont}>
            <Markable tokens={searchValue ?? ''}>{value}</Markable>
          </span>
        ) : null,
    },
    {
      title: 'Wichtige Info',
      dataIndex: 'importantDescription',
      key: 'importantDescription',
      width: 110,
      render: value => {
        if (!value) {
          return;
        }
        return (
          <span onClick={e => e.stopPropagation()}>
            <Popover trigger={['click']} content={<div style={{ whiteSpace: 'pre-wrap', maxWidth: '500px' }}>{value}</div>}>
              <Button
                type="link"
                className={css`
                  height: 20px;
                  margin: 0;
                  padding: 0;
                `}
                icon={<EyeOutlined />}
              />
            </Popover>
          </span>
        );
      },
    },
    {
      title: 'Beschreibung',
      dataIndex: 'description',
      key: 'description',
      width: 120,
      render: value => {
        if (!value) {
          return;
        }
        return (
          <span onClick={e => e.stopPropagation()}>
            <Popover trigger={['click']} content={<div style={{ whiteSpace: 'pre-wrap', maxWidth: '500px' }}>{value}</div>}>
              <Button
                type="link"
                className={css`
                  height: 20px;
                  margin: 0;
                  padding: 0;
                `}
                icon={<EyeOutlined />}
              />
            </Popover>
          </span>
        );
      },
    },
    {
      title: 'Verr.-Info',
      dataIndex: 'billingDescription',
      key: 'billingDescription',
      width: 90,
      render: value => {
        if (!value) {
          return;
        }
        return (
          <span onClick={e => e.stopPropagation()}>
            <Popover trigger={['click']} content={<div style={{ whiteSpace: 'pre-wrap', maxWidth: '500px' }}>{value}</div>}>
              <Button
                type="link"
                className={css`
                  height: 20px;
                  margin: 0;
                  padding: 0;
                `}
                icon={<EyeOutlined />}
              />
            </Popover>
          </span>
        );
      },
    },
    {
      title: 'KI-Beschr.',
      dataIndex: 'aiDescription',
      key: 'aiDescription',
      width: 110,
      render: value => {
        if (!value) {
          return;
        }
        return (
          <span onClick={e => e.stopPropagation()}>
            <Popover trigger={['click']} content={<div style={{ whiteSpace: 'pre-wrap', maxWidth: '500px' }}>{value}</div>}>
              <Button
                type="link"
                className={css`
                  height: 20px;
                  margin: 0;
                  padding: 0;
                `}
                icon={<EyeOutlined />}
              />
            </Popover>
          </span>
        );
      },
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      width: 80,
      render: value => {
        if (!value) {
          return;
        }
        return (
          <Button
            type="link"
            className={css`
              height: 20px;
              margin: 0;
              padding: 0;
            `}
            icon={<ExportOutlined />}
            onClick={e => {
              e.stopPropagation();
              openExternalLink(value);
            }}
          />
        );
      },
    },
    {
      title: 'Material',
      dataIndex: 'specimens',
      key: 'specimens',
      width: 100,
      ellipsis: true,
      render: (_, record) => <TableList entries={record.specimens.map(it => `${it.name} - ${it.classification}`)} maxEntries={3} />,
    },
    {
      title: 'Lagerdauer (Std.)',
      dataIndex: 'storagePeriod',
      key: 'storagePeriod',
      width: 160,
      sorter: (a, b) => (a.storagePeriod === b.storagePeriod ? 0 : a.storagePeriod > b.storagePeriod ? 1 : -1),
      render: value => (Math.sign(value) === -1 ? '' : <Tooltip title={humanizedHours(value)}>{value}</Tooltip>),
    },
    {
      title: 'Auswertung (Std.)',
      dataIndex: 'evalHours',
      key: 'evalHours',
      width: 160,
      sorter: (a, b) => (a.evalHours === b.evalHours ? 0 : a.evalHours > b.evalHours ? 1 : -1),
      render: value => (Math.sign(value) === -1 ? '' : <Tooltip title={humanizedHours(value)}>{value}</Tooltip>),
    },
    {
      title: 'Privat an Zuw.',
      dataIndex: 'doctorBilling',
      key: 'doctorBilling',
      width: 130,
      sorter: (a, b) => Number(a.doctorBilling) - Number(b.doctorBilling),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Privat an Patient',
      dataIndex: 'patientBilling',
      key: 'patientBilling',
      width: 160,
      sorter: (a, b) => Number(a.patientBilling) - Number(b.patientBilling),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'LG - Privat Zuw.',
      dataIndex: 'lgDoctorBilling',
      key: 'lgDoctorBilling',
      width: 150,
      sorter: (a, b) => Number(a.lgDoctorBilling) - Number(b.lgDoctorBilling),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Aktiv',
      dataIndex: 'deactivated',
      key: 'deactivated',
      width: 80,
      sorter: (a, b) => Number(b.deactivated) - Number(a.deactivated),
      render: value => (!value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Nur mit',
      dataIndex: 'withParameters',
      key: 'withParameters',
      width: 80,
      ellipsis: true,
      render: (_, record) => <TableList entries={record.withParameters.map(it => it.shortName)} maxEntries={3} mono />,
    },
    {
      title: 'Nicht mit',
      dataIndex: 'withoutParameters',
      key: 'withoutParameters',
      width: 80,
      ellipsis: true,
      render: (_, record) => <TableList entries={record.withoutParameters.map(it => it.shortName)} maxEntries={3} mono />,
    },
    {
      title: 'Sondertarife',
      dataIndex: 'specialRates',
      key: 'specialRates',
      width: 110,
      ellipsis: true,
      render: (_, record) => <TableList entries={record.specialRates.map(it => it.shortName)} maxEntries={3} />,
    },
    {
      title: 'Formulare',
      dataIndex: 'forms',
      key: 'forms',
      width: 110,
      ellipsis: true,
      render: (_, record) => <TableList entries={record.forms.map(it => it.name)} maxEntries={3} />,
    },
    {
      title: 'Preis Zuw.',
      dataIndex: 'priceDoctor',
      key: 'priceDoctor',
      width: 110,
      sorter: (a, b) => (a.priceDoctor ?? -1) - (b.priceDoctor ?? -1),
      render: value => <Price value={value} />,
    },
    {
      title: 'Preis Patient',
      dataIndex: 'pricePatient',
      key: 'pricePatient',
      width: 120,
      sorter: (a, b) => (a.pricePatient ?? -1) - (b.pricePatient ?? -1),
      render: value => <Price value={value} />,
    },
    {
      title: 'Preis LG-Zuw.',
      dataIndex: 'priceLgDoctor',
      key: 'priceLgDoctor',
      width: 140,
      sorter: (a, b) => (a.priceLgDoctor ?? -1) - (b.priceLgDoctor ?? -1),
      render: value => <Price value={value} />,
    },
    {
      title: 'Preis SB',
      dataIndex: 'priceDeductible',
      key: 'priceDeductible',
      width: 140,
      sorter: (a, b) => (a.priceDeductible ?? -1) - (b.priceDeductible ?? -1),
      render: value => <Price value={value} />,
    },
    {
      title: 'Partnerlabor',
      dataIndex: 'partnerLab',
      key: 'partnerLab',
      width: 130,
      sorter: (a, b) => a.partnerLab.localeCompare(b.partnerLab),
      render: value => (value === PartnerLab.NONE ? '' : translatePartnerLab(value, data?.lab)),
    },
    {
      title: 'Synonyme',
      dataIndex: 'synonyms',
      key: 'synonyms',
      width: 110,
      ellipsis: true,
      render: value => <TableList entries={value} maxEntries={3} />,
    },
    {
      title: 'Akut',
      dataIndex: 'acute',
      key: 'acute',
      width: 70,
      sorter: (a, b) => Number(a.acute) - Number(b.acute),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Volumen',
      dataIndex: 'volume',
      key: 'volume',
      width: 100,
      sorter: (a, b) => a.volume - b.volume,
    },
    {
      title: 'Analysestationen',
      dataIndex: 'analysisStations',
      key: 'analysisStations',
      width: 150,
      ellipsis: true,
      render: (_, record) => <TableList entries={record.analysisStations.map(it => it.name)} maxEntries={3} />,
    },
  ];

  const filteredColumns: ColumnsType<Parameter> = columnSettings
    .filter(cs => cs.visible)
    .map(cs => columns.find(c => c.key === cs.id)!)
    .filter(Boolean);

  return (
    <Flex
      vertical
      className={css`
        height: 100%;
        padding: ${token.paddingLG}px;
      `}
    >
      <Flex
        justify="flex-end"
        gap="small"
        className={css`
          margin-bottom: ${token.paddingSM}px;
        `}
      >
        <Input
          allowClear
          placeholder="Suche"
          onChange={e => debouncedSearch(e.target.value)}
          prefix={<SearchOutlined />}
          autoFocus
          suffix={
            <Tooltip title="Suche nach Kurzbezeichnung, Langbezeichnung, Edifact-Nummer oder Synonyme">
              <InfoCircleOutlined />
            </Tooltip>
          }
          style={{ width: '300px' }}
        />
        <Button type="primary" icon={<PlusCircleOutlined />} disabled={loading} onClick={() => setCreateModalOpen(true)}>
          Neuer Parameter
        </Button>
        <TableColumnSettings tableKey="parameters" initialColumnSettings={columnSettings} onChange={setColumnSettings} />
      </Flex>
      <div
        ref={contentRef}
        className={css`
          height: 100%;
          overflow: auto;
        `}
      >
        <Table<Parameter>
          scroll={{ x: 'max-content', y: tableHeight }}
          rowKey={record => record.id}
          size="middle"
          showSorterTooltip={false}
          dataSource={filteredParameters}
          onRow={record => ({ onClick: () => setEditId(record.id), style: { cursor: 'pointer' } })}
          pagination={{
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Parametern`,
            pageSizeOptions: ['50', '100', '500', '1000'],
            defaultPageSize: 50,
          }}
          loading={loading}
          columns={filteredColumns}
        />
      </div>
      <CreateParameterModal
        open={createModalOpen}
        onSuccess={() => refetch()}
        onClose={() => setCreateModalOpen(false)}
        labId={labId ?? ''}
        parameters={data?.labParameters ?? []}
      />
      <UpdateParameterModal
        parameterId={editId}
        parameters={data?.labParameters ?? []}
        labId={labId ?? ''}
        onSuccess={() => refetch()}
        onClose={() => setEditId(null)}
      />
    </Flex>
  );
};
